/**
 * Acquisition-source attribution helper.
 *
 *  1. captureSource() reads `?source=…` (or fallback aliases like `utm_source=`)
 *     from the current URL at app boot, normalises it, and persists it to
 *     localStorage with a 30-day TTL.
 *  2. getSource() returns the persisted source (or "direct" if none).
 *  3. clearSource() removes it (used after install / signup so we don't
 *     keep over-attributing).
 *
 *  Used by:
 *   • AuthContext.register()      → sends `source` to /api/auth/register
 *   • InstallPrompt + DownloadAppSection → POST /api/analytics/install
 */
const STORAGE_KEY = "neuracom_source";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Canonical buckets — mirror backend `ALLOWED_SOURCES`.
const ALLOWED = new Set([
  "qr",
  "install_banner",
  "install_ios",
  "share",
  "shortcut",
  "partner",
  "referral",
  "social",
  "email",
  "ads",
  "search",
  "direct",
  "other",
]);

const ALIASES = {
  "pwa-qr": "qr",
  pwaqr: "qr",
  qrcode: "qr",
  installbanner: "install_banner",
  banner: "install_banner",
  ios: "install_ios",
  cpc: "ads",
  ppc: "ads",
  googleads: "ads",
  adwords: "ads",
  google: "search",
  bing: "search",
  facebook: "social",
  twitter: "social",
  linkedin: "social",
  x: "social",
  instagram: "social",
  tiktok: "social",
  youtube: "social",
  newsletter: "email",
  ref: "referral",
  affiliate: "partner",
};

function normalize(raw) {
  if (!raw) return null;
  const s = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 32);
  if (!s) return null;
  if (ALLOWED.has(s)) return s;
  if (ALIASES[s]) return ALIASES[s];
  // strip dashes (qr-pwa → qrpwa → other)
  const noDash = s.replace(/-/g, "");
  if (ALLOWED.has(noDash)) return noDash;
  if (ALIASES[noDash]) return ALIASES[noDash];
  return "other";
}

/** Read `?source=` from URL and persist if found. Idempotent. */
export function captureSource() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const raw =
      url.searchParams.get("source") ||
      url.searchParams.get("utm_source") ||
      url.searchParams.get("ref"); // /p/{code} sets ?ref=
    const normalized = normalize(raw);
    if (!normalized) return;
    const payload = { v: normalized, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / disabled storage — silently no-op */
  }
}

/** Get the currently attributed source, or null if none / expired. */
export function getSource() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { v, ts } = JSON.parse(raw);
    if (!v || !ts || Date.now() - ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

/** Generate (and persist) a stable anonymous id used to dedupe install events. */
export function getAnonymousId() {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem("neuracom_anon_id");
    if (!id) {
      id =
        "anon_" +
        Math.random().toString(36).slice(2, 10) +
        Date.now().toString(36);
      localStorage.setItem("neuracom_anon_id", id);
    }
    return id;
  } catch {
    return null;
  }
}

/** Best-effort: classify install platform for analytics splits. */
export function detectInstallPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (ua.includes("mac") && navigator.maxTouchPoints > 1) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}
